import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, Sparkles,
  PlayCircle, Loader2, Clipboard, Database, CreditCard, Mail, Search, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import WaitlistAnalyticsWidget from '@/components/WaitlistAnalyticsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEV = {
  high:   { label: 'BLOCKER', bg: 'bg-red-500/15 text-red-300 border-red-500/30', icon: XCircle },
  medium: { label: 'WARN',    bg: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: AlertTriangle },
  low:    { label: 'INFO',    bg: 'bg-zinc-700/40 text-zinc-300 border-zinc-600', icon: AlertTriangle },
};

const STATUS_TONE = {
  pass: { color: 'text-green-400', bg: 'bg-green-500/15 border-green-500/30', label: 'PASS', dot: 'bg-green-400' },
  warn: { color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/30', label: 'WARN', dot: 'bg-amber-400' },
  fail: { color: 'text-red-400',   bg: 'bg-red-500/15 border-red-500/30',     label: 'FAIL', dot: 'bg-red-400'   },
  pending: { color: 'text-zinc-400', bg: 'bg-zinc-700/30 border-zinc-600',    label: '…',    dot: 'bg-zinc-500 animate-pulse' },
};

const SERVICE_META = {
  stripe:  { icon: CreditCard, label: 'Stripe',    spec: 'Balance.retrieve()' },
  ses:     { icon: Mail,       label: 'AWS SES',   spec: 'GetSendQuota' },
  fmcsa:   { icon: Search,     label: 'FMCSA',     spec: 'SAFER ping' },
  mongodb: { icon: Database,   label: 'MongoDB',   spec: 'Write+delete round-trip' },
};

const SERVICE_ORDER = ['stripe', 'ses', 'fmcsa', 'mongodb'];

const SetupStatusPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preflight, setPreflight] = useState(null);   // {checks:[...], completed_at, overall}
  const [preflightServices, setPreflightServices] = useState({}); // realtime overlay per service
  const [preflightRunning, setPreflightRunning] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [digestSending, setDigestSending] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);

  const sendDigestNow = useCallback(async () => {
    setDigestSending(true);
    try {
      const r = await axios.post(`${API}/admin/founder-digest/send-now?force=true`);
      setDigestPreview(r.data);
      const { sent, suppressed, error, counts = {}, to } = r.data || {};
      const tally = `${counts.movements ?? 0} moves · ${counts.follow_ups ?? 0} follow-ups · ${counts.opens ?? 0} opens · ${counts.outreach ?? 0} outreach`;
      if (sent) toast.success(`Digest sent to ${to} — ${tally}`);
      else if (suppressed) toast.warning(`Recipient suppressed — preview rendered (${tally})`);
      else if (error) toast.error(`SES error: ${error} — preview still rendered (${tally})`);
      else toast(`Digest built — ${tally}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest send failed');
    } finally {
      setDigestSending(false);
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/setup-status`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  const loadLastPreflight = async () => {
    try {
      const r = await axios.get(`${API}/admin/setup-status/preflight/last`);
      if (r.data && r.data.completed_at) {
        setPreflight(r.data);
        const map = {};
        (r.data.checks || []).forEach((c) => { map[c.service] = c; });
        setPreflightServices(map);
      }
    } catch { /* none yet */ }
  };

  useEffect(() => { load(); loadLastPreflight(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const runPreflight = useCallback(async () => {
    setPreflightRunning(true);
    // Optimistic UI: each service shows pending until the response arrives.
    const optimistic = {};
    SERVICE_ORDER.forEach((s) => { optimistic[s] = { service: s, status: 'pending', detail: 'Running…' }; });
    setPreflightServices(optimistic);
    try {
      const r = await axios.post(`${API}/admin/setup-status/preflight`);
      const map = {};
      (r.data.checks || []).forEach((c) => { map[c.service] = c; });
      setPreflightServices(map);
      setPreflight(r.data);
      if (r.data.overall === 'pass') {
        toast.success('All systems live ✓');
      } else if (r.data.overall === 'warn') {
        toast.warning('Preflight passed with warnings');
      } else {
        toast.error('Preflight detected failures');
      }
    } catch (e) {
      toast.error('Preflight request failed');
    } finally {
      setPreflightRunning(false);
    }
  }, []);

  const copyEnvChecklist = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/setup-status/env-checklist`);
      const text = r.data.template || '';
      if (!navigator.clipboard) {
        toast.error('Clipboard not available in this browser');
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.success(`Copied — paste into backend/.env (${r.data.total_missing_keys} keys)`);
    } catch {
      toast.error('Could not generate .env checklist');
    }
  }, []);

  if (loading || !data) {
    return (
      <Layout>
        <div className="p-8 text-muted-foreground">Loading setup status…</div>
      </Layout>
    );
  }

  const { summary, checklist } = data;
  const byCategory = checklist.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});

  const livePassCount = SERVICE_ORDER.filter((s) => preflightServices[s]?.status === 'pass').length;
  const liveAllGreen = preflight && livePassCount === SERVICE_ORDER.length;
  const productionTileColor = liveAllGreen ? 'text-green-400'
    : (summary.production_ready ? 'text-green-400' : 'text-red-400');
  const productionTileValue = liveAllGreen ? 'LIVE'
    : (summary.production_ready ? 'YES' : 'NO');

  const fmtAgo = (iso) => {
    if (!iso) return null;
    const diffMs = Math.max(0, now - new Date(iso).getTime());
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins === 1) return '1 minute ago';
    if (mins < 60) return `${mins} minutes ago`;
    const hrs = Math.round(mins / 60);
    return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="setup-status-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Setup Status</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Pre-flight check — everything Stripe / SES / Legal / Business needs before going live.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={load} data-testid="setup-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={copyEnvChecklist} data-testid="copy-env-checklist-btn">
              <Clipboard className="w-4 h-4 mr-2" /> Copy .env checklist
            </Button>
            <Button variant="outline" onClick={sendDigestNow} disabled={digestSending} data-testid="send-digest-now-btn">
              {digestSending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                : <><Send className="w-4 h-4 mr-2" /> Send digest now</>}
            </Button>
            <Button
              onClick={runPreflight}
              disabled={preflightRunning}
              data-testid="run-preflight-btn"
              className={liveAllGreen ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {preflightRunning
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                : <><PlayCircle className="w-4 h-4 mr-2" /> Run Preflight</>}
            </Button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatTile testId="stat-total" label="Total" value={summary.total} />
          <StatTile testId="stat-ok" label="OK" value={summary.ok} color="text-green-400" />
          <StatTile testId="stat-blockers" label="Blockers" value={summary.blockers} color="text-red-400" />
          <StatTile testId="stat-warnings" label="Warnings" value={summary.warnings} color="text-amber-400" />
          <StatTile
            testId="stat-production-ready"
            label="Production-ready"
            value={productionTileValue}
            color={productionTileColor}
            icon={Sparkles}
            subtitle={preflight ? `Live: ${livePassCount}/4` : 'Run Preflight to confirm'}
          />
        </div>

        {/* Founder digest preview (rendered after Send-digest-now click) */}
        {digestPreview && (
          <Card data-testid="digest-preview-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-400" /> Daily Founder Digest preview
                </CardTitle>
                <p className="text-[11px] text-muted-foreground mt-1">
                  To <strong>{digestPreview.to}</strong> · Subject: {digestPreview.subject}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {digestPreview.sent
                  ? <Badge className="bg-green-500/15 text-green-300 border-green-500/30">SENT</Badge>
                  : digestPreview.suppressed
                    ? <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">SUPPRESSED</Badge>
                    : <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-500/30">PREVIEW ONLY</Badge>}
                <Button size="sm" variant="ghost" onClick={() => setDigestPreview(null)} data-testid="dismiss-digest-preview-btn">Dismiss</Button>
              </div>
            </CardHeader>
            <CardContent>
              <iframe
                title="founder-digest-preview"
                srcDoc={digestPreview.html}
                data-testid="digest-preview-iframe"
                className="w-full border border-border rounded-md bg-white"
                style={{ height: '480px' }}
              />
            </CardContent>
          </Card>
        )}

        {/* Preflight live-status row */}
        <Card data-testid="preflight-card" className={liveAllGreen ? 'border-green-500/40 bg-green-500/5' : ''}>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Live Preflight</CardTitle>
              {preflight?.completed_at && (
                <p
                  className="text-[11px] text-muted-foreground mt-1"
                  data-testid="preflight-last-checked"
                >
                  Last checked: {fmtAgo(preflight.completed_at)}
                  {' · '}
                  duration {preflight.duration_ms ?? '—'} ms
                  {' · overall '}
                  <span className={STATUS_TONE[preflight.overall]?.color || ''}>
                    {STATUS_TONE[preflight.overall]?.label || preflight.overall?.toUpperCase()}
                  </span>
                </p>
              )}
              {!preflight && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  No live ping yet — click <strong>Run Preflight</strong> to call Stripe, SES, FMCSA and MongoDB.
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SERVICE_ORDER.map((svc) => {
              const meta = SERVICE_META[svc];
              const Icon = meta.icon;
              const rec = preflightServices[svc];
              const tone = STATUS_TONE[rec?.status] || (preflightRunning ? STATUS_TONE.pending : STATUS_TONE.pending);
              return (
                <div
                  key={svc}
                  className={`rounded-md border p-3 ${tone.bg}`}
                  data-testid={`preflight-service-${svc}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-semibold">{meta.label}</span>
                    </div>
                    <span className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                      <span
                        className={`text-[11px] font-semibold ${tone.color}`}
                        data-testid={`preflight-status-${svc}`}
                      >
                        {tone.label}
                      </span>
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mb-1">{meta.spec}</div>
                  {rec?.detail && (
                    <div className="text-[11px] text-zinc-200 break-words leading-snug" data-testid={`preflight-detail-${svc}`}>
                      {rec.detail}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Waitlist analytics — founder traction widget while Stripe/SES approvals pend */}
        <WaitlistAnalyticsWidget />

        {/* Static-config checklist */}
        {Object.entries(byCategory).map(([category, items]) => (
          <Card key={category} data-testid={`setup-category-${category.toLowerCase()}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{category}</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="divide-y divide-border">
                {items.map((c) => {
                  const sev = SEV[c.severity] || SEV.low;
                  const SevIcon = sev.icon;
                  const ok = c.status.ok;
                  return (
                    <div
                      key={c.key}
                      className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2"
                      data-testid={`setup-row-${c.key}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {ok
                            ? <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                            : <SevIcon className={`w-4 h-4 shrink-0 ${c.severity === 'high' ? 'text-red-400' : c.severity === 'medium' ? 'text-amber-400' : 'text-zinc-400'}`} />}
                          <div className="font-medium">{c.title}</div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{c.detail}</div>
                        {c.action && !ok && (
                          <div className="text-[11px] text-blue-300 mt-1.5" data-testid={`setup-action-${c.key}`}>
                            → {c.action}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          className={ok
                            ? 'bg-green-500/20 text-green-300 border-green-500/30'
                            : sev.bg}
                          data-testid={`setup-badge-${c.key}`}
                        >
                          {c.status.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </Layout>
  );
};

const StatTile = ({ label, value, color = 'text-foreground', icon: Icon, subtitle, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null} {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
      {subtitle && <div className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</div>}
    </CardContent>
  </Card>
);

export default SetupStatusPage;
